import { useEffect, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useSelectedAgentDraftController } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/selected-agent-drafts';
import {
	type MessageAction as APIMessageAction,
	type MessageActionInvocationStatus,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { type StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useActiveTaskForConversation } from '@atlassian/conversation-assistant-store/controllers/unified-tasks';

import { normalizeAgentIdentityValue } from '../utils/normalizeAgentIdentityValue';

import { useCsatEligibility } from './useCsatEligibility';
import { useCsatSurveyState } from './useCsatSurveyState';

type MessageAuthor = {
	id?: string;
	named_id?: string;
	external_config_reference?: string;
};

type AssistantResponseMessage = {
	actions?: APIMessageAction[] | null;
	async_status?: string | null;
	author?: MessageAuthor;
};
const TERMINAL_ACTION_STATUSES: ReadonlySet<MessageActionInvocationStatus> = new Set([
	'finished',
	'cancelled',
	'errored',
]);

type UseStoreAssistantCsatStateParams = {
	storeAssistant: StoreAssistant;
	assistantResponse: {
		message: AssistantResponseMessage;
	};
	invokedActions: Record<string, MessageActionInvocationStatus> | undefined;
	filteredActions: APIMessageAction[] | undefined;
	feedbackTypeV2: string | null | undefined;
	streamingComplete: boolean | undefined;
	shouldHideFooterButtons: boolean | undefined;
	isConversationActivelyGenerating: boolean;
	conversationId: string;
	agentMessageId: string;
	handleCsatFeedback: Parameters<typeof useCsatSurveyState>[0]['handleCsatFeedback'];
};

/**
 * Owns all CSAT-survey decision logic for a single `StoreAssistantView`:
 * eligibility gating (final response, no pending action card, no in-progress
 * task, default Rovo agent, no selected-agent draft) and the survey state
 * machine. A sticky per-message latch keeps CSAT hidden for any message that
 * ever contained an action awaiting user input; the next AI message mounts a
 * fresh hook instance and may show CSAT.
 */
export const useStoreAssistantCsatState = ({
	storeAssistant,
	assistantResponse,
	invokedActions,
	filteredActions,
	feedbackTypeV2,
	streamingComplete,
	shouldHideFooterButtons,
	isConversationActivelyGenerating,
	conversationId,
	agentMessageId,
	handleCsatFeedback,
}: UseStoreAssistantCsatStateParams): ReturnType<typeof useCsatSurveyState> => {
	const [activeTask] = useActiveTaskForConversation(conversationId);

	const isFinalResponse =
		storeAssistant.content.type === 'FINAL_RESPONSE' &&
		Boolean(streamingComplete) &&
		assistantResponse.message.async_status !== 'IN_PROGRESS';
	const isInteractionConclusionSignalEnabled = fg('rovo_chat_csat_interaction_conclusion_signal');
	const isSuccessfulInteractionConclusion =
		!isInteractionConclusionSignalEnabled ||
		storeAssistant.interactionConclusionOutcome === 'SUCCEEDED';

	const hasActionInTurn = Boolean(filteredActions?.length);
	const hasCompletedTaskOrNoTask =
		!hasActionInTurn || !activeTask || activeTask.progressState === 'Completed';

	const hasPendingActionInput = (assistantResponse.message.actions ?? []).some((action) => {
		const feStatus = invokedActions?.[action.invocationId];
		if (feStatus && TERMINAL_ACTION_STATUSES.has(feStatus)) {
			return false;
		}
		if (action.status && TERMINAL_ACTION_STATUSES.has(action.status)) {
			return false;
		}
		return true;
	});

	// Sticky per-message latch: if this message ever had a pending action,
	// CSAT stays suppressed here for its whole lifetime.
	const [hadPendingActionAtAnyPoint, setHadPendingActionAtAnyPoint] = useState(
		() => hasPendingActionInput,
	);
	useEffect(() => {
		if (hasPendingActionInput && !hadPendingActionAtAnyPoint) {
			setHadPendingActionAtAnyPoint(true);
		}
	}, [hasPendingActionInput, hadPendingActionAtAnyPoint]);

	// CSAT should only measure the default Rovo experience. Suppress when the
	// message was authored by a non-default agent, or when a selected-agent
	// draft is present (next turn will route away from default Rovo).
	const messageAuthor = assistantResponse.message.author;
	const isFromDefaultRovoAgent =
		normalizeAgentIdentityValue(
			messageAuthor?.external_config_reference ?? messageAuthor?.named_id ?? messageAuthor?.id,
		) === undefined;
	const { activeSelectedAgentId } = useSelectedAgentDraftController(conversationId);
	const shouldSuppressCsatForAgent = !isFromDefaultRovoAgent || activeSelectedAgentId !== undefined;

	const isRuntimeEligible =
		!storeAssistant.isHistorical &&
		!feedbackTypeV2 &&
		!isConversationActivelyGenerating &&
		isFinalResponse &&
		isSuccessfulInteractionConclusion &&
		!shouldHideFooterButtons &&
		hasCompletedTaskOrNoTask &&
		!hadPendingActionAtAnyPoint &&
		!shouldSuppressCsatForAgent;

	const { isEligibleToShow, onCsatInteracted, onCsatDisplayed } = useCsatEligibility({
		enabled: isRuntimeEligible,
	});

	const surveyState = useCsatSurveyState({
		conversationId,
		agentMessageId,
		handleCsatFeedback,
		isEligibleToShow,
		hadPendingActionAtAnyPoint,
		shouldSuppressCsatForAgent,
		onCsatInteracted,
		onCsatDisplayed,
	});

	const { isCsatSurveyVisible, recordCsatSurveyShown } = surveyState;
	useEffect(() => {
		if (isCsatSurveyVisible) {
			recordCsatSurveyShown();
		}
	}, [isCsatSurveyVisible, recordCsatSurveyShown]);

	return surveyState;
};
